import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { App, IVacancy } from '../../shared';
import { AddVacancy } from '../../shared/components/add_vacancy/add_vacancy';
import { PublishVacancyBan } from '../../shared/components/publish_vacancy_ban/publish_vacancy_ban';
import { UpdateVacancy } from '../../shared/components/update_vacancy/update_vacancy';
import { Vacancies } from '../../shared/components/vacancies/vacancies';
import { ISearchFilter } from '../../shared/components/vacancies_filter/vacancies_filter';
import { VacancyPreview } from '../../shared/components/vacancy_preview/vacancy_preview';
import { getHttpApi } from '@cian/http-api/browser';
import { getConfig } from '@cian/config/browser';
import { UNIQUE_CONFIG_KEY } from '../../shared/constants';
import { getLogger } from '@cian/logger/browser';
import { IUser } from '../../shared/types/user';
import { configureSentry } from '../sentry';
import { IRegion } from '../../shared/types/region';

export function runApp() {
  const config = getConfig();
  const httpApi = getHttpApi();
  const logger = getLogger();

  configureSentry(config, {
    matchUrl: url => {
      return url.indexOf(UNIQUE_CONFIG_KEY) >= 0;
    },
  }).then(() => {
    const rootElement = document.getElementById('frontend-vacancies');
    if (!rootElement) {
      throw new Error('Unable to find element #frontend-vacancies');
    }

    let component: React.ReactNode;

    const regions = config.get<IRegion[]>('regions');
    const vacancyJSON = config.get<IVacancy>('vacancyJSON');
    const vacanciesJSON = config.get<IVacancy[]>('vacanciesJSON');
    const regionsWithVacancy = config.get<IRegion[]>('regionsWithVacancy');
    const vacanciesCount = config.get<number>('vacanciesCount');
    const recaptchaSitekey = config.get<string>('recaptchaSitekey');
    const editBackUrlJSON = config.get<string>('editBackUrlJSON');
    const pageCount = config.get<number>('pageCount');
    const filterJSON = config.get<ISearchFilter>('filterJSON');
    const user = config.get<IUser>('user');
    const useRecaptcha = config.get<boolean>('useRecaptcha') || undefined;

    switch (config.get<string>('currentPage')) {
      case 'addVacancy':
        if (!user) {
          throw new Error('User is not configured');
        }
        if (!recaptchaSitekey) {
          throw new Error('recaptchaSitekey is not configured');
        }

        if (user.isAuthenticated && (!user.isProfi || !user.canPublishOffer || user.isBuilder)) {
          component = <PublishVacancyBan />;
        } else {
          if (!regions) {
            throw new Error('regions is not configured');
          }
          if (!user) {
            throw new Error('User is not configured');
          }

          component = (
            <AddVacancy
              httpApi={httpApi}
              logger={logger}
              recaptchaSitekey={recaptchaSitekey}
              useRecaptcha={useRecaptcha}
              user={user}
              regions={regions}
            />
          );
        }
        break;
      case 'updateVacancy':
        if (!vacancyJSON) {
          throw new Error('vacancyJSON is not configured');
        }
        if (!editBackUrlJSON) {
          throw new Error('editBackUrlJSON is not configured');
        }
        if (!regions) {
          throw new Error('regions is not configured');
        }
        if (!recaptchaSitekey) {
          throw new Error('recaptchaSitekey is not configured');
        }
        if (!user) {
          throw new Error('User is not configured');
        }

        component = (
          <UpdateVacancy
            httpApi={httpApi}
            logger={logger}
            regions={regions}
            editBackUrl={editBackUrlJSON}
            user={user}
            useRecaptcha={useRecaptcha}
            recaptchaSitekey={recaptchaSitekey}
            vacancy={vacancyJSON}
          />
        );
        break;
      case 'previewVacancy':
        if (!vacancyJSON) {
          throw new Error('vacancyJSON is not configured');
        }

        component = <VacancyPreview vacancy={vacancyJSON} />;
        break;
      case 'vacancies':
        if (!vacanciesJSON) {
          throw new Error('vacanciesJSON is not configured');
        }
        if (!filterJSON) {
          throw new Error('filterJSON is not configured');
        }
        if (!regionsWithVacancy) {
          throw new Error('regionsWithVacancy is not configured');
        }
        if (typeof vacanciesCount !== 'number') {
          throw new Error('vacanciesCount is not configured');
        }
        if (typeof pageCount !== 'number') {
          throw new Error('pageCount is not configured');
        }
        if (!user) {
          throw new Error('user is not configured');
        }
        document.cookie = `cian_vacancy_region=${filterJSON.region}`;

        component = (
          <Vacancies
            logger={logger}
            httpApi={httpApi}
            filter={filterJSON}
            user={user}
            vacanciesCount={vacanciesCount}
            pageCount={pageCount}
            regionsWithVacancy={regionsWithVacancy}
            vacancies={vacanciesJSON}
          />
        );
        break;
      default:
        component = <div />;
    }

    ReactDOM.createRoot(rootElement).render(<App component={component} />);
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((module as any).hot) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (module as any).hot.accept();
  }
}
